import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { EventItem } from '../../types';
import { EventPoster } from '../../pages/Home/HomeParts';
import { formatIndianDate } from '../../utils/dateFormat';
import { coalescePriceFromApiAndRawTiers } from '../../utils/eventMedia';
import { formatBookingOpensAt, getEventBookingWindowStatus } from '../../utils/eventBookingWindow';
import { ShareButton } from './ShareButton';
import { isMultiOccurrence, scheduleBadge, scheduleDateLabel } from '../../utils/occurrence';

interface EventCardProps {
  event: EventItem;
  variant?: 'default' | 'featured';
  index?: number;
}

const categoryLabels: Record<string, string> = {
  concert: 'Concert',
  festival: 'Festival',
  sports: 'Sports',
  comedy: 'Comedy',
  theatre: 'Theatre',
  family: 'Family',
  other: 'Event',
};

const EventCardBase: React.FC<EventCardProps> = ({ event, variant = 'default' }) => {
  const isFeatured = variant === 'featured' || event.featured;
  // Multi-date events show a range / "Multiple dates" instead of one stored date
  const multi = isMultiOccurrence(event);
  const dateFormatted = scheduleDateLabel(event) ?? formatIndianDate(event.date);
  const badge = scheduleBadge(event);
  const bookingStatus = getEventBookingWindowStatus(event);
  const tierCount = event.tierCount ?? 0;
  const priceFrom = coalescePriceFromApiAndRawTiers(event.priceFrom, (event.tiers ?? []) as unknown[]);

  return (
    <Link
      to={`/events/${event.slug}`}
      className="group block h-full outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0b0f] rounded-3xl"
    >
      <motion.article
        whileHover={{ y: -6 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 22 }}
        className={`relative flex h-full flex-col overflow-hidden rounded-3xl glass-panel glass-panel-hover ${
          isFeatured ? 'ring-1 ring-primary/25' : ''
        }`}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-black/50">
          <div className="zq-poster absolute inset-0 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105">
            <EventPoster event={event} />
          </div>

          <div className="absolute left-4 top-4 z-10 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-white/10 bg-black/55 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/95 backdrop-blur-md">
              {categoryLabels[event.category] || event.category}
            </span>
            {badge ? (
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/90 backdrop-blur-md">
                {badge}
              </span>
            ) : null}
            {isFeatured ? (
              <span className="rounded-full border border-primary/40 bg-primary/20 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white animate-pulse-slow">
                Featured
              </span>
            ) : null}
          </div>

          <div className="absolute bottom-4 right-4 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full bg-primary opacity-0 shadow-[0_0_24px_rgba(229,57,53,0.45)] transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5 lg:p-6">
          <div className="flex items-start justify-between gap-2">
            <p className="text-caption uppercase tracking-[0.2em] text-white/45">
              {dateFormatted}
              {multi ? null : (
                <>
                  <span className="mx-2 inline-block h-1 w-1 rounded-full bg-primary/60 align-middle" aria-hidden />
                  <span className="text-white/55">{event.time}</span>
                </>
              )}
            </p>
            <ShareButton event={event} variant="icon" />
          </div>
          <h3 className="mt-2 text-h3 text-white line-clamp-2 transition-colors duration-300 group-hover:text-primary">
            {event.title}
          </h3>
          <p className="mt-2 flex items-start gap-2 text-caption text-white/50">
            <svg className="mt-0.5 h-4 w-4 shrink-0 text-primary/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="line-clamp-2">
              {event.venue}, {event.city}
            </span>
          </p>
          <div className="mt-auto flex flex-col gap-1 pt-4">
            <div className="flex items-baseline gap-2">
              <span className="text-eyebrow uppercase text-white/35">
                From
              </span>
              <p className="text-xl font-medium tracking-tighter text-primary">₹{priceFrom}</p>
              <span className="text-sm font-light text-white/40">/ ticket</span>
            </div>
            {tierCount > 0 && (
              <p className="text-caption text-white/45">
                {tierCount} ticket tier{tierCount === 1 ? '' : 's'}
              </p>
            )}
          </div>
          {bookingStatus === 'not_yet_open' && event.bookingStartDate ? (
            <p className="mt-3 rounded-lg border border-amber-500/20 bg-amber-950/35 px-3 py-2 text-caption text-amber-100/90">
              Tickets go live {formatBookingOpensAt(event.bookingStartDate)}
            </p>
          ) : null}
          {bookingStatus === 'closed' ? (
            <p className="mt-3 text-caption text-white/45">Ticket sales have ended</p>
          ) : null}
        </div>
      </motion.article>
    </Link>
  );
};

export const EventCard = React.memo(EventCardBase);
